import { RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import { Input } from "../../../components/input";
import { SelectV2 } from "../../../components/select-v2";
import { DataTable, type DataTableColumn } from "../../../components/table/data-table";
import { useWalletTransactions } from "../hooks/useWalletTransactions";
export default function WalletTransactionsPage() {
  const { t } = useTranslation();
  const state = useWalletTransactions();
  const pages = Math.max(1, Math.ceil(state.totalCount / state.size));
  const columns: DataTableColumn<(typeof state.items)[number]>[] = [
    {
      key: "user",
      title: t("wallet_transactions.user"),
      render: (item) => (
        <>
          <b>{item.userName}</b>
          <br />
          <span className="text-xs text-gray-500">{item.userEmail}</span>
        </>
      ),
    },
    { key: "type", title: t("wallet_transactions.type"), render: (item) => item.type },
    {
      key: "amount",
      title: t("wallet_transactions.amount"),
      render: (item) => (
        <span
          className={
            item.amount >= 0 ? "font-semibold text-emerald-700" : "font-semibold text-red-700"
          }
        >
          {item.amount.toLocaleString()} pts
        </span>
      ),
    },
    { key: "status", title: t("wallet_transactions.status"), render: (item) => item.status },
    { key: "source", title: t("wallet_transactions.source"), render: (item) => item.sourceType },
    {
      key: "created",
      title: t("wallet_transactions.created_at"),
      render: (item) => new Date(item.createdAt).toLocaleString(),
    },
  ];
  const selects = [
    [state.type, state.setType, ["all", "credit", "debit", "refund", "adjustment"]],
    [state.status, state.setStatus, ["all", "completed", "pending", "failed"]],
    [state.source, state.setSource, ["all", "event", "survey", "coupon", "admin", "other"]],
  ] as const;
  return (
    <main className="p-4 md:p-6">
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold">{t("wallet_transactions.title")}</h1>
          <p className="text-sm text-gray-500">{t("wallet_transactions.description")}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => void state.refetch()}
          disabled={state.loading}
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          {t("common.refresh")}
        </Button>
      </div>
      {state.summary && (
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          {[
            [t("wallet_transactions.total"), state.summary.totalTransactions],
            [t("wallet_transactions.credit"), state.summary.creditPoints],
            [t("wallet_transactions.debit"), state.summary.debitPoints],
          ].map(([label, value]) => (
            <div
              key={label as string}
              className="rounded-xl border bg-white p-4 dark:bg-gray-900"
            >
              <p className="text-sm text-gray-500">{label}</p>
              <p className="text-2xl font-bold">{Number(value).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}
      <div className="mb-4 flex flex-wrap gap-3">
        <Input
          value={state.name}
          onChange={(event) => state.setName(event.target.value)}
          placeholder={t("wallet_transactions.search")}
          className="w-full max-w-[300px]"
        />
        {selects.map(([value, setValue, options], index) => (
          <SelectV2
            key={index}
            value={value}
            onChange={setValue}
            options={options.map((option) => ({
              value: option,
              label: t(`wallet_transactions.${option}`),
            }))}
            placeholder={t("wallet_transactions.all")}
            ariaLabel={t("wallet_transactions.all")}
            className="w-full sm:w-52"
          />
        ))}
      </div>
      <DataTable
        columns={columns}
        dataSource={state.items}
        rowKey="id"
        loading={state.loading}
        error={state.error}
        onRetry={() => void state.refetch()}
        emptyLabel={t("wallet_transactions.empty")}
        pagination={{ page: state.page, totalPages: pages, onPageChange: state.setPage }}
      />
      {/*
          <div className="overflow-x-auto rounded-xl border">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase">
                <tr>
                  <th className="p-4">{t("wallet_transactions.user")}</th>
                  <th className="p-4">{t("wallet_transactions.type")}</th>
                  <th className="p-4">{t("wallet_transactions.amount")}</th>
                  <th className="p-4">{t("wallet_transactions.status")}</th>
                  <th className="p-4">{t("wallet_transactions.source")}</th>
                  <th className="p-4">{t("wallet_transactions.created_at")}</th>
                </tr>
              </thead>
              <tbody>
                {state.items.map((item) => (
                  <tr
                    key={item.id}
                    className="border-t"
                  >
                    <td className="p-4">
                      <b>{item.userName}</b>
                      <br />
                      <span className="text-xs text-gray-500">{item.userEmail}</span>
                    </td>
                    <td className="p-4">{item.type}</td>
                    <td
                      className={
                        item.amount >= 0
                          ? "p-4 font-semibold text-emerald-700"
                          : "p-4 font-semibold text-red-700"
                      }
                    >
                      {item.amount.toLocaleString()} pts
                    </td>
                    <td className="p-4">{item.status}</td>
                    <td className="p-4">{item.sourceType}</td>
                    <td className="p-4">{new Date(item.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!state.items.length && (
            <p className="p-8 text-center text-gray-500">{t("wallet_transactions.empty")}</p>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={state.page === 1 || state.loading}
              onClick={() => state.setPage(state.page - 1)}
            >
              {t("sync_points.previous")}
            </Button>
            <span className="self-center">
              {state.page} / {pages}
            </span>
            <Button
              type="button"
              variant="outline"
              disabled={state.page >= pages || state.loading}
              onClick={() => state.setPage(state.page + 1)}
            >
              {t("sync_points.next")}
            </Button>
          </div>
      */}
    </main>
  );
}
